import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSpinner } from "@fortawesome/free-solid-svg-icons"
import { faFileExclamation } from "@fortawesome/pro-regular-svg-icons"
import { faVideo } from "@fortawesome/pro-solid-svg-icons"
import WordThumbnail from "../../../static/assets/images/microsoft-word-icon.svg"
import ExcelThumbnail from "../../../static/assets/images/microsoft-excel-icon.svg"
import PowerpointThumbnail from "../../../static/assets/images/microsoft-powerpoint-icon.svg"
import useCheckIfMobile from "../hooks/useCheckIfMobile"
import convertMimeToExtension from "./convertMimeToExtension"
import "./FileDisplayer.css"

/**
 * Displays a user-uploaded file
 * @param {userFile} file
 * @param {boolean} isThumbnail
 */
const FileDisplayer = ({file, isThumbnail}) => {
  const { isMobile } = useCheckIfMobile()

  const fileURL = file.fileURL
  const fileType = file.fileType
  const thumbnailURL = file.thumbnailURL
  const c = isThumbnail ? "-thumbnail" : ""

  let fileDisplayElement = <FontAwesomeIcon className="loading-spinner" icon={faSpinner} spin size="lg" />

  if (file.errorMessage) {
    fileDisplayElement = (
      <>
        <FontAwesomeIcon icon={faFileExclamation} color="#8B0000" size="4x"/>
        <div className="error-message">
          ERROR
        </div>
      </>
    )
  } else if (file.isLoading) {
  } else if (["image/jpg", "image/jpeg", "image/png", "image/webp", "image/gif", "image/svg+xml", "image/apng"].includes(fileType)) {
    fileDisplayElement = <img className={`image${c}`} src={fileURL} alt="user uploaded content"/>
  } else if (fileType === "video/mp4" && fileURL && isThumbnail && isMobile) {
    // many mobile browsers won't render video thumbnails
    fileDisplayElement = <FontAwesomeIcon icon={faVideo} size="4x"/>
  } else if (fileType === "video/mp4" && fileURL){
    fileDisplayElement = <video className={`video${c}`} src={fileURL} {...(isThumbnail ? {} : {controls: true})}/>
  } else if (fileType === "application/pdf" && isThumbnail && thumbnailURL) {
    fileDisplayElement = <img className="image-thumbnail" src={file.thumbnailURL} alt="user uploaded content"/>
  } else if (fileType === "application/pdf" && !isThumbnail && fileURL) {
    // on mobile browsers that can't display pdfs, just display the thumbnail
    fileDisplayElement = (
      <object className="pdf" data={fileURL} width="60%" height="100%">
        <img className="image-thumbnail" src={file.thumbnailURL} alt="user uploaded content"/>
      </object>
    )
  } else if (["application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(fileType) && isThumbnail) {
    fileDisplayElement = <img className={`word-icon`} src={WordThumbnail} alt="user uploaded word document"/> 
  } else if (["application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"].includes(fileType) && isThumbnail) {
    fileDisplayElement = <img className={`excel-icon`} src={ExcelThumbnail} alt="user uploaded excel spreadsheet"/> 
  } else if (["application/vnd.ms-powerpoint", "application/vnd.openxmlformats-officedocument.presentationml.presentation"].includes(fileType) && isThumbnail) {
    fileDisplayElement = <img className={`powerpoint-icon`} src={PowerpointThumbnail} alt="user uploaded powerpoint presentation"/> 
  } else if (["application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-powerpoint", "application/vnd.openxmlformats-officedocument.presentationml.presentation"].includes(fileType) && file.fileId && file.userId) {
    // For more info, check the documentation of the downloadStoryboardFile firebase function
    const fileExtension = convertMimeToExtension(file.fileType)
    const linkToFile = `https://us-central1-${process.env.GATSBY_FIREBASE_PROJECT_ID}.cloudfunctions.net/downloadStoryboardFile/${file.userId}/${file.fileId}.${fileExtension}`
    fileDisplayElement = (
      <>
      <div id="storyboard-file-loading-spinner-container">
        <FontAwesomeIcon className="loading-spinner" icon={faSpinner} spin size="lg" />
      </div>
      <iframe 
        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(linkToFile)}`} 
        className="microsoft-web-viewer"
        height="100%"
        width="100%" 
        frameborder='0'
        onLoad={() => {
          if (typeof document !== "undefined") {
            const loadingSpinnerElement = document.getElementById("storyboard-file-loading-spinner-container")
            loadingSpinnerElement.style.display = "none"
          }
        }}
      />
      </>
    )
  }

  return (
    <div className="file-displayer">
      {fileDisplayElement}
    </div>
  )
}

export default FileDisplayer
